<template>
  <SrpModal :isVisible="props.isVisible" @update:isVisible="isVisible => emit('update:isVisible', isVisible)" size="medium">
    <template #content>
      <LoadingIcon v-if="isLoadingQueue" :size="28" />

      <template v-else-if="overlapQueueActive">
        <h2 class="global-h2 choose-creator-date-modal__title">Visit Order and Creator Selection Dates</h2>
        <p class="global-p choose-creator-date-modal__intro">
          - One visit can be sent to creators at a time.<br />- Creators have {{ DaysCreatorsHaveToApply }} days to apply (you should choose as soon as possible after that).
        </p>

        <OverlappingVisitsQueuePanel
          v-model:queue="queueRows"
          :currentCollabInputId="props.collabInputId"
          :hasPostedVisitAwaitingCreator="queueFlags.hasPostedVisitAwaitingCreator"
          :hasReadyVisitNotYetPosted="queueFlags.hasReadyVisitNotYetPosted"
        />
      </template>

      <ChooseCreatorDateForm
        v-else
        class="choose-creator-date-form"
        v-model:chooseCreatorEstimatedDate="_chooseCreatorEstimatedDate"
        v-model:chooseCreatorEstimatedNotes="_chooseCreatorEstimatedNotes"
      />
    </template>

    <template #footer="{ closeModal }">
      <SrpButton fill="outlined" color="gray" @click="closeModal" :isDisabled="isSaving">Cancel</SrpButton>

      <SrpButton @click="save(closeModal)" :isDisabled="isSaving || isLoadingQueue || !isSaveEnabled">
        <template #icon>
          <LoadingIcon v-if="isSaving" :size="18" />
          <IconEmbedded v-else name="save_2" :size="22" color="rgba(255, 255, 255, 0.4)" />
        </template>
        Save
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from "vue";
import moment from "moment";

import SrpModal from "@components/ui/SrpModal.vue";
import ChooseCreatorDateForm from "./ChooseCreatorDateForm.vue";
import OverlappingVisitsQueuePanel from "./OverlappingVisitsQueuePanel.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import CollabsRepo from "@repos/CollabsRepo";
import { CollabInput } from "@contracts/collab";
import { DaysCreatorsHaveToApply } from "@constants/collab";
import { buildScheduleUpdatePayload, formatChooseCreatorDateForInput, hasOverlappingQueuePeers, isQueueDateValid, OverlappingVisitQueueRow } from "./overlappingVisitsQueueLogic";

const props = withDefaults(
  defineProps<{
    isVisible: boolean;
    chooseCreatorEstimatedDate: string | null;
    chooseCreatorEstimatedNotes: string | null;
    saveCallback: () => Promise<void>;
    communityId: string;
    collabInputId: string;
    enableOverlappingPostsLogic: boolean;
  }>(),
  {
    isVisible: false,
    chooseCreatorEstimatedDate: null,
    chooseCreatorEstimatedNotes: null,
    saveCallback: async () => {},
    enableOverlappingPostsLogic: false,
  }
);

const emit = defineEmits<{
  (e: "update:isVisible", isVisible: boolean): void;
  (e: "update:chooseCreatorEstimatedDate", value: string): void;
  (e: "update:chooseCreatorEstimatedNotes", value: string | null): void;
  (e: "scheduleUpdated", updatedCollabInputs: CollabInput[]): void;
}>();

const isSaving = ref<boolean>(false);
const isLoadingQueue = ref(false);
const queueRows = ref<OverlappingVisitQueueRow[]>([]);
const queueFlags = ref({ hasPostedVisitAwaitingCreator: false, hasReadyVisitNotYetPosted: false });

const _chooseCreatorEstimatedDate = ref<string>("");
const _chooseCreatorEstimatedNotes = ref<string | null>(null);

const overlapQueueActive = computed(() => props.enableOverlappingPostsLogic && hasOverlappingQueuePeers(queueRows.value, props.collabInputId));

const isSaveEnabled = computed(() => {
  if (overlapQueueActive.value) return isQueueDateValid(queueRows.value);
  if (!_chooseCreatorEstimatedDate.value) return false;
  return moment(_chooseCreatorEstimatedDate.value).isSameOrAfter(moment().startOf("day"), "day");
});

onMounted(() => {
  _chooseCreatorEstimatedDate.value = props.chooseCreatorEstimatedDate;
  _chooseCreatorEstimatedNotes.value = props.chooseCreatorEstimatedNotes;
});

watch(
  () => props.isVisible,
  visible => {
    if (visible) loadQueue();
  },
  { immediate: true }
);

watch(
  queueRows,
  rows => {
    const current = rows.find(r => r.id === props.collabInputId);
    if (current?.chooseCreatorEstimatedDate) {
      emit("update:chooseCreatorEstimatedDate", current.chooseCreatorEstimatedDate);
    }
    if (current) {
      emit("update:chooseCreatorEstimatedNotes", current.chooseCreatorEstimatedNotes);
    }
  },
  { deep: true }
);

async function loadQueue() {
  if (!props.enableOverlappingPostsLogic || !props.communityId) {
    queueRows.value = [];
    return;
  }

  isLoadingQueue.value = true;
  const response = await CollabsRepo.loadVisitsMarkedReadyToPost(props.communityId);
  queueFlags.value = {
    hasPostedVisitAwaitingCreator: response.hasPostedVisitAwaitingCreator,
    hasReadyVisitNotYetPosted: response.hasReadyVisitNotYetPosted,
  };
  const rows: OverlappingVisitQueueRow[] = response.visits.map(v => ({
    ...v,
    chooseCreatorEstimatedDate: formatChooseCreatorDateForInput(v.chooseCreatorEstimatedDate),
    isCurrent: v.id === props.collabInputId,
  }));
  queueRows.value = hasOverlappingQueuePeers(rows, props.collabInputId) ? rows : [];
  isLoadingQueue.value = false;
}

async function save(closeModalCallback: () => void) {
  isSaving.value = true;

  if (overlapQueueActive.value) {
    const { updatedCollabInputs } = await CollabsRepo.updateOverlappingVisitsSchedule(props.communityId, { updates: buildScheduleUpdatePayload(queueRows.value) });
    emit("scheduleUpdated", updatedCollabInputs);
    const current = updatedCollabInputs.find(c => c.id === props.collabInputId);
    if (current?.chooseCreatorEstimatedDate) {
      emit("update:chooseCreatorEstimatedDate", current.chooseCreatorEstimatedDate);
      emit("update:chooseCreatorEstimatedNotes", current.chooseCreatorEstimatedNotes);
    }
  } else {
    emit("update:chooseCreatorEstimatedDate", _chooseCreatorEstimatedDate.value);
    emit("update:chooseCreatorEstimatedNotes", _chooseCreatorEstimatedNotes.value);
  }

  await props.saveCallback();
  isSaving.value = false;
  closeModalCallback();
}
</script>

<style scoped lang="scss">
.choose-creator-date-modal {
  &__title {
    margin-bottom: 10px;
  }

  &__intro {
    margin-bottom: 20px;
  }
}

.choose-creator-date-form {
  :deep(.when-can-you-choose-form__title) {
    font-size: 35px;
    line-height: 38px;
  }
}
</style>
